import { Content, Page, ErrorPanel } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { MetricsBreadcrumbs } from './MetricsBreadcrumbs';

interface TribeQuarterlyMetric {
  tribe: string;
  quarter_label: string;
  year: number;
  quarter: number;
  avg_coverage: number | null;
  coverage_variance: number | null;
  avg_reliability: number | null;
  reliability_variance: number | null;
  avg_security: number | null;
  security_variance: number | null;
  avg_maintainability: number | null;
  maintainability_variance: number | null;
  total_smells: number | null;
  smells_variance: number | null;
}

// --- Helpers ---

/**
 * Convert a numeric SonarQube rating (1–5) to a letter grade.
 * 1 = A (best), 5 = E (worst).
 */
const getRatingGrade = (value: number | null): string => {
  if (value === null) return '—';
  if (value <= 1.5) return 'A';
  if (value <= 2.5) return 'B';
  if (value <= 3.5) return 'C';
  if (value <= 4.5) return 'D';
  return 'E';
};

/** Pick the most recent quarter row for each tribe. */
const getLatestQuarterPerTribe = (data: TribeQuarterlyMetric[]): TribeQuarterlyMetric[] => {
  const map = new Map<string, TribeQuarterlyMetric>();
  for (const row of data) {
    const existing = map.get(row.tribe);
    const yr = Number(row.year);
    const qr = Number(row.quarter);
    if (
      !existing ||
      yr > Number(existing.year) ||
      (yr === Number(existing.year) && qr > Number(existing.quarter))
    ) {
      map.set(row.tribe, row);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.tribe.localeCompare(b.tribe));
};

const formatCoverage = (v: number | null): string =>
  v !== null ? `${Number(v).toFixed(1)}%` : '—';

const formatCount = (v: number | null): string =>
  v !== null ? Number(v).toLocaleString() : '—';

const formatVariance = (
  v: number | null,
  higherIsBetter: boolean,
): { label: string; color: string; arrow: string } => {
  if (v === null) return { label: '—', color: '#94a3b8', arrow: '' };
  const improving = higherIsBetter ? v >= 0 : v <= 0;
  const sign = v > 0 ? '+' : '';
  const arrow = v === 0 ? '' : v > 0 ? ' ↑' : ' ↓';
  return {
    label: `${sign}${Number(v).toFixed(2)}`,
    color: improving ? '#15803d' : '#b91c1c',
    arrow,
  };
};

const useStyles = makeStyles(theme => ({
  dashboard: {
    minHeight: '100%',
    padding: theme.spacing(3),
    backgroundColor: '#f6f8fb',
    color: '#172033',
    borderRadius: 16,
  },
  hero: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(3),
    borderRadius: 18,
    border: '1px solid #d9e2ef',
    background: 'linear-gradient(135deg, #ffffff 0%, #eef5ff 100%)',
    boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)',
  },
  eyebrow: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 10px',
    marginBottom: theme.spacing(1.25),
    borderRadius: 999,
    color: '#1d4ed8',
    backgroundColor: '#eaf2ff',
    border: '1px solid #c7dcff',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  heroTitle: {
    color: '#0f172a',
    fontWeight: 800,
    fontSize: 30,
    lineHeight: 1.18,
    marginBottom: theme.spacing(1),
  },
  heroText: {
    maxWidth: 820,
    color: '#475569',
    fontSize: 15,
    lineHeight: 1.65,
  },
  tabsRoot: {
    marginBottom: theme.spacing(2.5),
    borderBottom: '1px solid #dce6f2',
    backgroundColor: 'transparent',
  },
  tabRoot: {
    color: '#64748b',
    fontWeight: 700,
    fontSize: 14,
    textTransform: 'none',
    minWidth: 0,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    '&$tabSelected': { color: '#1d4ed8' },
  },
  tabSelected: {},
  tabIndicator: {
    backgroundColor: '#1d4ed8',
    height: 3,
    borderRadius: '3px 3px 0 0',
  },
  statCard: {
    height: '100%',
    borderRadius: 16,
    border: '1px solid #dce6f2',
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
  },
  statLabel: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.45,
    textTransform: 'uppercase',
  },
  statValue: {
    color: '#0f172a',
    fontSize: 30,
    fontWeight: 800,
    marginTop: theme.spacing(0.75),
  },
  statSubText: {
    color: '#64748b',
    fontSize: 13,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.45,
  },
  card: {
    height: '100%',
    borderRadius: 16,
    border: '1px solid #dce6f2',
    backgroundColor: '#ffffff',
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
  },
  cardContent: {
    padding: theme.spacing(2.25),
    '&:last-child': { paddingBottom: theme.spacing(2.25) },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.75),
    paddingBottom: theme.spacing(1.25),
    borderBottom: '1px solid #edf2f7',
  },
  titleGroup: { minWidth: 0 },
  title: {
    color: '#0f172a',
    fontWeight: 800,
    fontSize: 18,
    lineHeight: 1.25,
  },
  subtitle: {
    color: '#64748b',
    fontSize: 13,
    marginTop: theme.spacing(0.5),
    lineHeight: 1.45,
  },
  countChip: {
    flexShrink: 0,
    height: 30,
    color: '#1e3a8a',
    fontWeight: 800,
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
  },
  tableContainer: {
    maxHeight: 520,
    overflowX: 'auto',
    borderRadius: 14,
    border: '1px solid #dce6f2',
    backgroundColor: '#ffffff',
    boxShadow: 'none',
  },
  table: { backgroundColor: '#ffffff' },
  tableHeadCell: {
    top: 0,
    color: '#334155',
    fontWeight: 800,
    fontSize: 12,
    letterSpacing: 0.35,
    textTransform: 'uppercase',
    backgroundColor: '#f1f5f9',
    borderBottom: '1px solid #cbd5e1',
    whiteSpace: 'nowrap' as const,
    padding: '13px 16px',
  },
  tableCell: {
    color: '#334155',
    fontSize: 14,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #edf2f7',
    whiteSpace: 'nowrap' as const,
    padding: '13px 16px',
    verticalAlign: 'middle',
  },
  tableRow: {
    '&:nth-of-type(even) td': { backgroundColor: '#fbfdff' },
    '&:hover td': { backgroundColor: '#eef6ff' },
  },
  primaryCell: { color: '#0f172a', fontWeight: 800 },
  mutedCell: { color: '#64748b' },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 160,
    borderRadius: 14,
    color: '#64748b',
    backgroundColor: '#f8fafc',
    border: '1px dashed #cbd5e1',
  },
  loadingBox: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 360,
    borderRadius: 18,
    backgroundColor: '#ffffff',
    border: '1px solid #dce6f2',
    boxShadow: '0 8px 22px rgba(15, 23, 42, 0.06)',
  },
  loadingText: {
    color: '#475569',
    marginTop: theme.spacing(2),
    fontWeight: 700,
  },
  // SonarQube A–E rating badge colours
  gradeA: { color: '#166534', backgroundColor: '#dcfce7', border: '1px solid #86efac', fontWeight: 800, height: 26 },
  gradeB: { color: '#065f46', backgroundColor: '#d1fae5', border: '1px solid #6ee7b7', fontWeight: 800, height: 26 },
  gradeC: { color: '#92400e', backgroundColor: '#fef3c7', border: '1px solid #fde68a', fontWeight: 800, height: 26 },
  gradeD: { color: '#9a3412', backgroundColor: '#ffedd5', border: '1px solid #fed7aa', fontWeight: 800, height: 26 },
  gradeE: { color: '#991b1b', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', fontWeight: 800, height: 26 },
  gradeNA: { color: '#64748b', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0', fontWeight: 800, height: 26 },
}));

// --- Shared sub-components ---

type StyleClasses = ReturnType<typeof useStyles>;

const RatingGradeChip = ({ value, classes }: { value: number | null; classes: StyleClasses }) => {
  const grade = getRatingGrade(value);
  const styleMap: Record<string, string> = {
    A: classes.gradeA,
    B: classes.gradeB,
    C: classes.gradeC,
    D: classes.gradeD,
    E: classes.gradeE,
    '—': classes.gradeNA,
  };
  return (
    <Tooltip title={value !== null ? `Raw avg: ${Number(value).toFixed(2)}` : 'No data'} arrow>
      <Chip size="small" label={grade} className={styleMap[grade] ?? classes.gradeNA} />
    </Tooltip>
  );
};

const VarianceDisplay = ({
  value,
  higherIsBetter,
}: {
  value: number | null;
  higherIsBetter: boolean;
}) => {
  const { label, color, arrow } = formatVariance(value, higherIsBetter);
  return (
    <span style={{ color, fontWeight: 700, fontSize: 13 }}>
      {label}{arrow}
    </span>
  );
};

const SummaryCard = ({
  label,
  value,
  helper,
  classes,
}: {
  label: string;
  value: number | string;
  helper: string;
  classes: StyleClasses;
}) => (
  <Card className={classes.statCard}>
    <CardContent className={classes.cardContent}>
      <Typography className={classes.statLabel}>{label}</Typography>
      <Typography className={classes.statValue}>{value}</Typography>
      <Typography className={classes.statSubText}>{helper}</Typography>
    </CardContent>
  </Card>
);

// --- Latest-quarter overview table: all 5 metrics per tribe in one row ---

const LatestQuarterOverviewTable = ({
  rows,
  classes,
}: {
  rows: TribeQuarterlyMetric[];
  classes: StyleClasses;
}) => {
  if (rows.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="body2">No data available</Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size="small" stickyHeader className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell className={classes.tableHeadCell}>Tribe</TableCell>
            <TableCell className={classes.tableHeadCell}>Quarter</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Coverage</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Cov Δ</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Reliability</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Rel Δ</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Security</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Sec Δ</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Maintainability</TableCell>
            <TableCell className={classes.tableHeadCell} align="center">Maint Δ</TableCell>
            <TableCell className={classes.tableHeadCell} align="right">Code Smells</TableCell>
            <TableCell className={classes.tableHeadCell} align="right">Smells Δ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={`${row.tribe}-${idx}`} className={classes.tableRow}>
              <TableCell className={classes.tableCell}>
                <span className={classes.primaryCell}>{row.tribe}</span>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <span className={classes.mutedCell}>{row.quarter_label}</span>
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                {formatCoverage(row.avg_coverage)}
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <VarianceDisplay value={row.coverage_variance} higherIsBetter={true} />
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <RatingGradeChip value={row.avg_reliability} classes={classes} />
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <VarianceDisplay value={row.reliability_variance} higherIsBetter={false} />
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <RatingGradeChip value={row.avg_security} classes={classes} />
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <VarianceDisplay value={row.security_variance} higherIsBetter={false} />
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <RatingGradeChip value={row.avg_maintainability} classes={classes} />
              </TableCell>
              <TableCell className={classes.tableCell} align="center">
                <VarianceDisplay value={row.maintainability_variance} higherIsBetter={false} />
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                {formatCount(row.total_smells)}
              </TableCell>
              <TableCell className={classes.tableCell} align="right">
                <VarianceDisplay value={row.smells_variance} higherIsBetter={false} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

// --- Quarterly trend table for a single metric ---

const MetricTrendTable = ({
  title,
  subtitle,
  data,
  getValue,
  getVariance,
  valueHeader,
  formatValue,
  higherIsBetter,
  classes,
}: {
  title: string;
  subtitle: string;
  data: TribeQuarterlyMetric[];
  getValue: (d: TribeQuarterlyMetric) => number | null;
  getVariance: (d: TribeQuarterlyMetric) => number | null;
  valueHeader: string;
  formatValue: (v: number | null) => string;
  higherIsBetter: boolean;
  classes: StyleClasses;
}) => {
  const sorted = data
    .filter(row => getValue(row) !== null)
    .sort((a, b) => {
      if (a.tribe !== b.tribe) return a.tribe.localeCompare(b.tribe);
      if (Number(a.year) !== Number(b.year)) return Number(a.year) - Number(b.year);
      return Number(a.quarter) - Number(b.quarter);
    });

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.sectionHeader}>
          <Box className={classes.titleGroup}>
            <Typography className={classes.title}>{title}</Typography>
            <Typography className={classes.subtitle}>{subtitle}</Typography>
          </Box>
          <Chip
            size="small"
            label={`${new Set(sorted.map(r => r.tribe)).size} tribes`}
            className={classes.countChip}
          />
        </Box>

        {sorted.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">No data available</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size="small" stickyHeader className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeadCell}>Tribe</TableCell>
                  <TableCell className={classes.tableHeadCell}>Quarter</TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">{valueHeader}</TableCell>
                  <TableCell className={classes.tableHeadCell} align="right">QoQ Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sorted.map((row, idx) => (
                  <TableRow key={`${row.tribe}-${row.quarter_label}-${idx}`} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.primaryCell}>{row.tribe}</span>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <span className={classes.mutedCell}>{row.quarter_label}</span>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      {formatValue(getValue(row))}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="right">
                      <VarianceDisplay value={getVariance(row)} higherIsBetter={higherIsBetter} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

// --- Section: summary + overview + 5 trend tables ---

const ClassificationSection = ({
  data,
  classes,
}: {
  data: TribeQuarterlyMetric[];
  classes: StyleClasses;
}) => {
  const latestRows = useMemo(() => getLatestQuarterPerTribe(data), [data]);

  const totalTribes = useMemo(() => new Set(data.map(d => d.tribe)).size, [data]);

  const avgCoverageLatest = useMemo(() => {
    const values = latestRows
      .map(r => r.avg_coverage)
      .filter((v): v is number => v !== null);
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
  }, [latestRows]);

  const improvingCoverageCount = useMemo(
    () => latestRows.filter(r => r.coverage_variance !== null && r.coverage_variance > 0).length,
    [latestRows],
  );

  const tribesNeedingAttention = useMemo(
    () =>
      latestRows.filter(r => {
        let degrading = 0;
        if (r.coverage_variance !== null && r.coverage_variance < 0) degrading++;
        if (r.reliability_variance !== null && r.reliability_variance > 0) degrading++;
        if (r.security_variance !== null && r.security_variance > 0) degrading++;
        if (r.maintainability_variance !== null && r.maintainability_variance > 0) degrading++;
        if (r.smells_variance !== null && r.smells_variance > 0) degrading++;
        return degrading >= 2;
      }).length,
    [latestRows],
  );

  if (data.length === 0) {
    return (
      <Box mt={2} className={classes.emptyState}>
        <Typography variant="body2">No data available for this classification</Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Grid container spacing={2}>
        {/* Summary stat cards */}
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Tribes tracked"
            value={totalTribes}
            helper="Unique tribes with quarterly data"
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Avg coverage (latest Q)"
            value={avgCoverageLatest !== null ? `${avgCoverageLatest.toFixed(1)}%` : '—'}
            helper="Mean code coverage across all tribes"
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Improving coverage"
            value={improvingCoverageCount}
            helper="Tribes with positive coverage Δ this quarter"
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            label="Need attention"
            value={tribesNeedingAttention}
            helper="Tribes degrading in ≥2 metrics this quarter"
            classes={classes}
          />
        </Grid>

        {/* Latest quarter cross-metric overview */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
              <Box className={classes.sectionHeader}>
                <Box className={classes.titleGroup}>
                  <Typography className={classes.title}>Latest quarter — all metrics</Typography>
                  <Typography className={classes.subtitle}>
                    Most recent quarter per tribe · Ratings shown as A–E (A = best) · Δ = quarter-over-quarter change
                  </Typography>
                </Box>
                <Chip size="small" label={`${latestRows.length} tribes`} className={classes.countChip} />
              </Box>
              <LatestQuarterOverviewTable rows={latestRows} classes={classes} />
            </CardContent>
          </Card>
        </Grid>

        {/* Per-metric trend tables */}
        <Grid item xs={12} md={6}>
          <MetricTrendTable
            title="Code Coverage"
            subtitle="Average code coverage per tribe per quarter — higher is better"
            data={data}
            getValue={d => d.avg_coverage !== null ? Number(d.avg_coverage) : null}
            getVariance={d => d.coverage_variance !== null ? Number(d.coverage_variance) : null}
            valueHeader="Avg Coverage"
            formatValue={v => v !== null ? `${Number(v).toFixed(2)}%` : '—'}
            higherIsBetter={true}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricTrendTable
            title="Code Reliability Index"
            subtitle="Avg reliability rating · 1 = A (best), 5 = E (worst) · lower is better"
            data={data}
            getValue={d => d.avg_reliability !== null ? Number(d.avg_reliability) : null}
            getVariance={d => d.reliability_variance !== null ? Number(d.reliability_variance) : null}
            valueHeader="Avg Reliability"
            formatValue={v => v !== null ? Number(v).toFixed(2) : '—'}
            higherIsBetter={false}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricTrendTable
            title="Security Index"
            subtitle="Avg security rating · 1 = A (best), 5 = E (worst) · lower is better"
            data={data}
            getValue={d => d.avg_security !== null ? Number(d.avg_security) : null}
            getVariance={d => d.security_variance !== null ? Number(d.security_variance) : null}
            valueHeader="Avg Security"
            formatValue={v => v !== null ? Number(v).toFixed(2) : '—'}
            higherIsBetter={false}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <MetricTrendTable
            title="Maintainability Index"
            subtitle="Avg SQALE rating · 1 = A (best), 5 = E (worst) · lower is better"
            data={data}
            getValue={d => d.avg_maintainability !== null ? Number(d.avg_maintainability) : null}
            getVariance={d => d.maintainability_variance !== null ? Number(d.maintainability_variance) : null}
            valueHeader="Avg Maintainability"
            formatValue={v => v !== null ? Number(v).toFixed(2) : '—'}
            higherIsBetter={false}
            classes={classes}
          />
        </Grid>
        <Grid item xs={12}>
          <MetricTrendTable
            title="Code Smells"
            subtitle="Total code smells per tribe per quarter — lower is better"
            data={data}
            getValue={d => d.total_smells !== null ? Number(d.total_smells) : null}
            getVariance={d => d.smells_variance !== null ? Number(d.smells_variance) : null}
            valueHeader="Total Smells"
            formatValue={v => v !== null ? Number(v).toLocaleString() : '—'}
            higherIsBetter={false}
            classes={classes}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export const SonarTribeViewPage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [activeData, setActiveData] = useState<TribeQuarterlyMetric[]>([]);
  const [legacyActiveData, setLegacyActiveData] = useState<TribeQuarterlyMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const baseUrl = await discoveryApi.getBaseUrl('metrics');
        const [activeRes, legacyActiveRes] = await Promise.all([
          fetchApi.fetch(`${baseUrl}/tribes/quarterly?classification=Active`),
          fetchApi.fetch(`${baseUrl}/tribes/quarterly?classification=Legacy Active`),
        ]);

        if (!activeRes.ok) throw new Error(`Failed to load active data: ${activeRes.statusText}`);
        if (!legacyActiveRes.ok) throw new Error(`Failed to load legacy active data: ${legacyActiveRes.statusText}`);

        setActiveData(await activeRes.json());
        setLegacyActiveData(await legacyActiveRes.json());
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [discoveryApi, fetchApi]);

  if (loading) {
    return (
      <Page themeId="tool">

        <Content>
          <Box className={classes.dashboard}>
            <Box className={classes.loadingBox}>
              <CircularProgress />
              <Typography className={classes.loadingText}>
                Loading tribe quarterly metrics…
              </Typography>
            </Box>
          </Box>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">

        <Content>
          <ErrorPanel error={new Error(error)} />
        </Content>
      </Page>
    );
  }

  const activeTribes = new Set(activeData.map(d => d.tribe)).size;
  const legacyTribes = new Set(legacyActiveData.map(d => d.tribe)).size;

  return (
    <Page themeId="tool">

      <Content>
        <Box className={classes.dashboard}>
          <MetricsBreadcrumbs currentPage="Tribe Quality KPIs" />
          {/* Hero */}
          <Box className={classes.hero}>
            <Typography className={classes.eyebrow}>Code quality dashboard</Typography>
            <Typography className={classes.heroTitle}>
              Tribe quarterly KPIs
            </Typography>
            <Typography className={classes.heroText}>
              Quarter-over-quarter code coverage, reliability, security, maintainability and code
              smell trends across all tribes. Ratings A–E correspond to ratings 1–5
              (A&nbsp;=&nbsp;best). Δ columns show the change from the previous quarter.
            </Typography>
          </Box>

          {/* Active / Legacy Active tabs */}
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            classes={{ root: classes.tabsRoot, indicator: classes.tabIndicator }}
          >
            <Tab
              label={`Active projects (${activeTribes} tribe${activeTribes === 1 ? '' : 's'})`}
              classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
            />
            <Tab
              label={`Legacy active projects (${legacyTribes} tribe${legacyTribes === 1 ? '' : 's'})`}
              classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
            />
          </Tabs>

          {tab === 0 && <ClassificationSection data={activeData} classes={classes} />}
          {tab === 1 && <ClassificationSection data={legacyActiveData} classes={classes} />}
        </Box>
      </Content>
    </Page>
  );
};
